import { useState, useEffect } from "react";
import { Play, Pause, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";

interface BeatCardProps {
  beat: {
    id: string;
    title: string;
    producer: string;
    genre: string;
    bpm: number;
    mp3_lease_price: number;
    wav_lease_price?: number;
    flp_price?: number;
    semi_exclusive_price?: number;
    full_exclusive_price?: number;
    image_url?: string;
    mp3_url?: string;
    wav_url?: string;
    is_sold_exclusive?: boolean;
  };
  onPlay?: () => void;
  isPlaying?: boolean;
  onClick?: () => void;
}

export const BeatCard = ({ beat, onPlay, isPlaying = false, onClick }: BeatCardProps) => {
  const [enabledLicenses, setEnabledLicenses] = useState<string[]>([]);

  useEffect(() => {
    const fetchLicensePreferences = async () => {
      const { data } = await supabase
        .from("beat_license_preferences")
        .select("license_type")
        .eq("beat_id", beat.id)
        .eq("is_enabled", true);
      
      if (data && data.length > 0) {
        setEnabledLicenses(data.map(pref => pref.license_type));
      } else {
        // Default to all available licenses if no preferences found
        const allLicenses = [];
        if (beat.mp3_lease_price !== undefined && beat.mp3_lease_price >= 0) allLicenses.push("mp3_lease");
        if (beat.wav_lease_price !== undefined && beat.wav_lease_price >= 0) allLicenses.push("wav_lease");
        if (beat.flp_price !== undefined && beat.flp_price >= 0) allLicenses.push("flp");
        if (beat.semi_exclusive_price !== undefined && beat.semi_exclusive_price >= 0) allLicenses.push("semi_exclusive");
        if (beat.full_exclusive_price !== undefined && beat.full_exclusive_price >= 0) allLicenses.push("full_exclusive");
        setEnabledLicenses(allLicenses);
      }
    };

    fetchLicensePreferences();
  }, [beat.id]);

  // Compute available prices from enabled licenses (ignore null/undefined and missing files)
  const availablePrices: number[] = [];
  if (enabledLicenses.includes('mp3_lease') && beat.mp3_url && typeof beat.mp3_lease_price === 'number' && beat.mp3_lease_price >= 0) availablePrices.push(beat.mp3_lease_price);
  if (enabledLicenses.includes('wav_lease') && beat.wav_url && typeof beat.wav_lease_price === 'number' && beat.wav_lease_price >= 0) availablePrices.push(beat.wav_lease_price);
  if (enabledLicenses.includes('flp') && typeof beat.flp_price === 'number' && beat.flp_price >= 0) availablePrices.push(beat.flp_price);
  if (enabledLicenses.includes('semi_exclusive') && typeof beat.semi_exclusive_price === 'number' && beat.semi_exclusive_price >= 0) availablePrices.push(beat.semi_exclusive_price);
  if (enabledLicenses.includes('full_exclusive') && typeof beat.full_exclusive_price === 'number' && beat.full_exclusive_price >= 0) availablePrices.push(beat.full_exclusive_price);
  
  const hasPurchasableOptions = availablePrices.length > 0;
  const minPrice = hasPurchasableOptions ? Math.min(...availablePrices) : undefined;
  const isFree = hasPurchasableOptions && availablePrices.some(p => p === 0);

  const handlePurchase = () => {
    // Set URL fragment to trigger license animation in modal
    window.location.hash = '#buy';
    
    // Open beat details modal
    onClick?.();
  };

  return (
    <Card className={`beat-card group overflow-hidden ${beat.is_sold_exclusive ? 'opacity-75' : 'cursor-pointer'}`} onClick={beat.is_sold_exclusive ? undefined : onClick}>
      <CardContent className="p-0">
        <div className="relative overflow-hidden">
          <img
            src={beat.image_url || '/placeholder.svg'}
            alt={`${beat.title} beat artwork`}
            className={`w-full h-48 object-cover transition-smooth ${beat.is_sold_exclusive ? 'grayscale' : 'group-hover:scale-105'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-smooth" />
          
          {/* Sold Exclusive Overlay */}
          {beat.is_sold_exclusive && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/50">
              <div className="bg-destructive/90 text-destructive-foreground px-4 py-2 rounded-lg font-bold text-lg">
                SOLD EXCLUSIVE
              </div>
            </div>
          )}
          
          {/* Play Button Overlay */}
          {!beat.is_sold_exclusive && (
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-smooth">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onPlay?.();
                }}
                size="lg"
                className="rounded-full w-16 h-16 bg-primary/90 hover:bg-primary shadow-glow"
              >
                {isPlaying ? (
                  <Pause className="h-6 w-6" />
                ) : (
                  <Play className="h-6 w-6 ml-1" />
                )}
              </Button>
            </div>
          )}
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-lg text-foreground truncate">{beat.title}</h3>
            {beat.is_sold_exclusive ? (
              <span className="text-destructive font-bold text-sm">SOLD</span>
            ) : (
              <span className="text-primary font-bold text-lg">
                {isFree ? 'FREE' : (typeof minPrice === 'number' ? `from $${minPrice}` : '')}
              </span>
            )}
          </div>
          
          <div className="flex items-center justify-between mb-2">
            <p className="text-muted-foreground text-sm">by {beat.producer}</p>
            <div className="flex gap-2 text-xs text-muted-foreground">
              <span>{beat.genre}</span>
              <span>•</span>
              <span>{beat.bpm} BPM</span>
            </div>
          </div>
          
          {!beat.is_sold_exclusive && (
            <div className="flex gap-2">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onPlay?.();
                }}
                variant="outline"
                size="sm"
                className="flex-1"
              >
                {isPlaying ? (
                  <Pause className="h-4 w-4 mr-2" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                {isPlaying ? 'Pause' : 'Play'}
              </Button>
              {hasPurchasableOptions && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePurchase();
                  }}
                  size="sm"
                  className="flex-1 group relative overflow-hidden"
                >
                  <ShoppingCart className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                  {isFree ? 'Download' : 'Buy'}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};